import React, { useState } from 'react';
import { contactAPI } from '../../utils/api';
import Button from '../common/Button';
import ErrorMessage from '../common/ErrorMessage';
import { validateForm } from '../../utils/validation';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validationRules = {
      name: [{ type: 'required', message: 'Name is required' }],
      email: [
        { type: 'required', message: 'Email is required' },
        { type: 'email', message: 'Please enter a valid email' },
      ],
      subject: [{ type: 'required', message: 'Subject is required' }],
      message: [
        { type: 'required', message: 'Message is required' },
        { type: 'minLength', value: 10, message: 'Message must be at least 10 characters' },
      ],
    };

    const validation = validateForm(formData, validationRules);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // Submit form
    try {
      setSubmitting(true);
      setSubmitError(null);
      await contactAPI.send(formData);
      setSubmitSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (err) {
      setSubmitError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {submitSuccess && (
        <div className="bg-accent-alt/10 border-2 border-accent-alt text-secondary px-5 py-4 rounded-sm font-body">
          Thank you for your message! We'll get back to you soon.
        </div>
      )}

      {submitError && (
        <ErrorMessage
          message={submitError}
          onClose={() => setSubmitError(null)}
        />
      )}

      {/* Name */}
      <div>
        <label htmlFor="name" className="block font-body font-semibold text-secondary mb-2 text-sm md:text-base">
          Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-4 py-3 md:py-3.5 border-2 rounded-sm font-body text-base focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all duration-300 ${
            errors.name ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          placeholder="Your name"
        />
        {errors.name && (
          <p className="mt-2 text-sm text-red-600 font-body">{errors.name}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block font-body font-semibold text-secondary mb-2 text-sm md:text-base">
          Email *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-4 py-3 md:py-3.5 border-2 rounded-sm font-body text-base focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all duration-300 ${
            errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          placeholder="your.email@example.com"
        />
        {errors.email && (
          <p className="mt-2 text-sm text-red-600 font-body">{errors.email}</p>
        )}
      </div>

      {/* Subject */}
      <div>
        <label htmlFor="subject" className="block font-body font-semibold text-secondary mb-2 text-sm md:text-base">
          Subject *
        </label>
        <input
          type="text"
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          className={`w-full px-4 py-3 md:py-3.5 border-2 rounded-sm font-body text-base focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all duration-300 ${
            errors.subject ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          placeholder="What's this about?"
        />
        {errors.subject && (
          <p className="mt-2 text-sm text-red-600 font-body">{errors.subject}</p>
        )}
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="block font-body font-semibold text-secondary mb-2 text-sm md:text-base">
          Message *
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={6}
          className={`w-full px-4 py-3 md:py-3.5 border-2 rounded-sm font-body text-base focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all duration-300 resize-y ${
            errors.message ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          placeholder="Tell us more about your project..."
        />
        {errors.message && (
          <p className="mt-2 text-sm text-red-600 font-body">{errors.message}</p>
        )}
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        disabled={submitting}
        className="w-full py-4 text-base md:text-lg font-semibold"
      >
        {submitting ? 'Sending...' : 'Send Message'}
      </Button>
    </form>
  );
};

export default ContactForm;
